/**
 * Display formatting and client-side search for the listings overview.
 *
 * Everything here is presentation only — no listing field is written, no
 * request is shaped. The overview reads what the admin list endpoint already
 * returns plus the cities and units the page already fetches.
 */

/**
 * The city as a person would say it, falling back down the chain the row can
 * actually offer. `cities` is the list the page already loads for the form's
 * dropdown, so this needs no extra request.
 */
export function cityLabel(listing, cities) {
  const match = (Array.isArray(cities) ? cities : []).find(
    (c) => c.id === listing.city_id
  );
  return (
    match?.name_de ||
    match?.name_en ||
    listing.city_code ||
    listing.city_id ||
    "—"
  );
}

const CHF = new Intl.NumberFormat("de-CH", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Swiss thousands formatting, without a currency prefix — the column header
 * already says "Preis (CHF)", so repeating it in every cell is noise.
 *
 * The separator glyph is whatever the runtime's ICU emits for de-CH (an
 * apostrophe, but the exact codepoint has differed between ICU builds), which
 * is why the tests compare against this same formatter rather than a literal.
 */
export function formatChf(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return CHF.format(n);
}

/**
 * The primary image of a listing, or null.
 *
 * `_listing_to_admin_shape` sorts images `(-is_main, position)`, so index 0 is
 * already the main image and the URL is already resolved server-side. A row
 * with no images, or one whose URL resolved to an empty string, has no
 * thumbnail — the caller renders a placeholder for both.
 */
export function primaryImageUrl(listing) {
  const first = Array.isArray(listing.images) ? listing.images[0] : null;
  const url = first?.url;
  return typeof url === "string" && url.trim() !== "" ? url : null;
}

/**
 * Case-insensitive substring search across the fields a person would actually
 * type: both titles, the slug, the address, the city (raw code and resolved
 * name) and the room name.
 *
 * Filtering runs over the in-memory list — `GET /api/admin/listings` returns
 * every row of the organization uncapped — so there is no request to debounce
 * and no server search to add.
 */
export function listingMatchesQuery(listing, query, { cities, objectLabel } = {}) {
  const needle = (query || "").trim().toLowerCase();
  if (!needle) return true;

  const haystack = [
    listing.title_de,
    listing.title_en,
    listing.slug,
    listing.address,
    listing.city_code,
    listing.room_name,
    cityLabel(listing, cities),
    objectLabel,
  ];

  return haystack.some(
    (v) => typeof v === "string" && v.toLowerCase().includes(needle)
  );
}
